import type { AnswerError, Lang } from '../logic/form-logic';

/**
 * Every word the patient form says that is not the pharmacy's own.
 *
 * Typed as `Record<Lang, FormCopy>`, so a string added in English without its
 * French is a compile error: a silent fallback to English is noticed only when
 * a patient in Quebec notices it.
 */
export interface FormCopy {
  questionOf: (position: number, total: number) => string;
  required: string;
  back: string;
  next: string;
  toReview: string;
  send: string;
  sending: string;
  reviewTitle: string;
  reviewIntro: string;
  change: string;
  changeAnswer: (label: string) => string;
  notAnswered: string;
  yes: string;
  no: string;
  agree: string;
  chooseAll: string;
  chooseUpTo: (n: number) => string;
  charactersLeft: (n: number) => string;
  infoHeading: string;
  signatureHelp: string;
  signatureCanvas: string;
  signatureDrawn: string;
  clearSignature: string;
  typeInstead: string;
  drawInstead: string;
  typedName: string;
  typedNameHelp: string;
  yourSignature: string;
  problemTitle: string;
  fixBeforeSending: string;
  sendFailed: string;
  previewNotice: string;
  previewSent: string;
  /** "Unable to continue" (SFM-D27): an answer that stops an online booking. */
  stopTitle: string;
  stopChange: string;
  /** Followed by the pharmacy's number, as a link. */
  stopCall: string;
  stopPreview: string;
  /** An optional form inside the booking. */
  skip: string;
  /** The last button while the booking takes the answers (nothing is sent yet). */
  working: string;
  errors: Record<AnswerError, string>;
  belowMin: (min: string) => string;
  aboveMax: (max: string) => string;
}

export const FORM_COPY: Record<Lang, FormCopy> = {
  en: {
    questionOf: (position, total) => `Question ${position} of ${total}`,
    required: 'Required',
    back: 'Back',
    next: 'Next',
    toReview: 'Check my answers',
    send: 'Send my answers',
    sending: 'Sending…',
    reviewTitle: 'Check your answers',
    reviewIntro:
      'Check your answers before you send them. You can change any of them.',
    change: 'Change',
    changeAnswer: (label) => `Change your answer to “${label}”`,
    notAnswered: 'Not answered',
    yes: 'Yes',
    no: 'No',
    agree: 'I agree',
    chooseAll: 'Choose all that apply.',
    chooseUpTo: (n) => `Choose up to ${n}.`,
    charactersLeft: (n) =>
      n === 1 ? '1 character left' : `${n} characters left`,
    infoHeading: 'Please read this',
    signatureHelp:
      'Sign in the box with your finger or a mouse, or type your full name instead.',
    signatureCanvas: 'Signature box',
    signatureDrawn: 'Signature added.',
    clearSignature: 'Clear signature',
    typeInstead: 'Type my full name instead',
    drawInstead: 'Sign in the box instead',
    typedName: 'Your full name',
    typedNameHelp: 'Typing your full name counts as your signature.',
    yourSignature: 'Your signature',
    problemTitle: 'There is a problem',
    fixBeforeSending: 'Fix these answers before you send the form:',
    sendFailed:
      'We could not send your answers. Check your connection and try again.',
    previewNotice: 'Preview: nothing you enter here is saved or sent.',
    previewSent: 'This is where the patient sends their answers.',
    stopTitle: 'Unable to continue',
    stopChange: 'Change my answer',
    stopCall: 'Questions? Call the pharmacy:',
    stopPreview:
      'Preview: booking online, the patient cannot go on with this answer. On a form sent by link, they read this after sending, and it is flagged for you.',
    skip: 'Skip this form for now',
    working: 'One moment…',
    errors: {
      required: 'Answer this question to continue.',
      invalid: 'This answer cannot be used. Check it and try again.',
      too_long: 'This answer is too long. Shorten it and try again.',
      not_an_option: 'Choose one of the options.',
      too_many: 'You chose too many. Untick one or more.',
      not_a_date: 'Enter a real date.',
      date_in_future: 'The date must be today or in the past.',
      date_in_past: 'The date must be today or in the future.',
      not_a_number: 'Enter a number, like 12 or 3.5.',
      below_min: 'This number is too small.',
      above_max: 'This number is too big.',
      not_integer: 'Enter a whole number, without decimals.',
      must_agree: 'Tick “I agree” to continue, or ask the pharmacy about it.',
      signature_missing: 'Sign in the box, or type your full name instead.',
      name_required: 'Type your full name.',
    },
    belowMin: (min) => `Enter a number of at least ${min}.`,
    aboveMax: (max) => `Enter a number of at most ${max}.`,
  },
  fr: {
    questionOf: (position, total) => `Question ${position} sur ${total}`,
    required: 'Obligatoire',
    back: 'Retour',
    next: 'Suivant',
    toReview: 'Vérifier mes réponses',
    send: 'Envoyer mes réponses',
    sending: 'Envoi en cours…',
    reviewTitle: 'Vérifiez vos réponses',
    reviewIntro:
      'Vérifiez vos réponses avant de les envoyer. Vous pouvez modifier chacune d’elles.',
    change: 'Modifier',
    changeAnswer: (label) => `Modifier votre réponse à « ${label} »`,
    notAnswered: 'Sans réponse',
    yes: 'Oui',
    no: 'Non',
    agree: 'J’accepte',
    chooseAll: 'Choisissez toutes les réponses qui s’appliquent.',
    chooseUpTo: (n) => `Choisissez jusqu’à ${n} réponses.`,
    charactersLeft: (n) =>
      n <= 1 ? `${n} caractère restant` : `${n} caractères restants`,
    infoHeading: 'Veuillez lire ceci',
    signatureHelp:
      'Signez dans le cadre avec votre doigt ou une souris, ou tapez plutôt votre nom complet.',
    signatureCanvas: 'Cadre de signature',
    signatureDrawn: 'Signature ajoutée.',
    clearSignature: 'Effacer la signature',
    typeInstead: 'Taper plutôt mon nom complet',
    drawInstead: 'Signer plutôt dans le cadre',
    typedName: 'Votre nom complet',
    typedNameHelp: 'Taper votre nom complet tient lieu de signature.',
    yourSignature: 'Votre signature',
    problemTitle: 'Il y a un problème',
    fixBeforeSending:
      'Corrigez ces réponses avant d’envoyer le formulaire :',
    sendFailed:
      'Nous n’avons pas pu envoyer vos réponses. Vérifiez votre connexion et réessayez.',
    previewNotice:
      'Aperçu : rien de ce que vous saisissez ici n’est enregistré ni envoyé.',
    previewSent: 'C’est ici que le patient envoie ses réponses.',
    stopTitle: 'Impossible de continuer',
    stopChange: 'Modifier ma réponse',
    stopCall: 'Des questions? Appelez la pharmacie :',
    stopPreview:
      'Aperçu : en réservant en ligne, le patient ne peut pas continuer avec cette réponse. Sur un formulaire envoyé par lien, il lit ceci après l’envoi, et la réponse vous est signalée.',
    skip: 'Passer ce formulaire pour l’instant',
    working: 'Un instant…',
    errors: {
      required: 'Répondez à cette question pour continuer.',
      invalid: 'Cette réponse ne peut pas être utilisée. Vérifiez-la et réessayez.',
      too_long: 'Cette réponse est trop longue. Raccourcissez-la et réessayez.',
      not_an_option: 'Choisissez une des réponses proposées.',
      too_many: 'Vous avez choisi trop de réponses. Décochez-en une ou plusieurs.',
      not_a_date: 'Entrez une date valide.',
      date_in_future: 'La date doit être aujourd’hui ou dans le passé.',
      date_in_past: 'La date doit être aujourd’hui ou dans le futur.',
      not_a_number: 'Entrez un nombre, par exemple 12 ou 3,5.',
      below_min: 'Ce nombre est trop petit.',
      above_max: 'Ce nombre est trop grand.',
      not_integer: 'Entrez un nombre entier, sans décimales.',
      must_agree:
        'Cochez « J’accepte » pour continuer, ou parlez-en à la pharmacie.',
      signature_missing:
        'Signez dans le cadre, ou tapez plutôt votre nom complet.',
      name_required: 'Tapez votre nom complet.',
    },
    belowMin: (min) => `Entrez un nombre d’au moins ${min}.`,
    aboveMax: (max) => `Entrez un nombre d’au plus ${max}.`,
  },
};
